// Modal dla globalnego narzutu/rabatu na noclegi

import React, { useState, useEffect } from "react";
import { useAccommodation } from "../../AccommodationProvider";
import { MarkupSettings } from "../../types/pricing.types";

interface GlobalMarkupModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const GlobalMarkupModal: React.FC<GlobalMarkupModalProps> = ({
  isOpen,
  onClose,
}) => {
  const { markup, applyMarkup, assignedRooms } = useAccommodation();

  // Stan formularza
  const [markupType, setMarkupType] = useState<"percentage" | "amount">(
    markup?.accommodationMarkupType || "percentage"
  );
  const [markupValue, setMarkupValue] = useState<number>(
    markup?.accommodationMarkupValue || 0
  );
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Synchronizuj lokalny stan z wartościami z Context gdy się zmienią
  useEffect(() => {
    if (markup) {
      setMarkupType(markup.accommodationMarkupType);
      setMarkupValue(markup.accommodationMarkupValue);
    }
  }, [markup]);

  // Oblicz podgląd kosztów
  const costPreview = React.useMemo(() => {
    if (!assignedRooms || assignedRooms.length === 0) {
      return {
        originalCost: 0,
        markupAmount: 0,
        totalCost: 0,
      };
    }

    const originalCost = assignedRooms.reduce(
      (sum, room) => sum + room.quantity * (room.unitPrice || 0),
      0
    );

    let markupAmount = 0;
    if (markupType === "percentage") {
      markupAmount = (originalCost * markupValue) / 100;
    } else {
      markupAmount = markupValue;
    }

    return {
      originalCost,
      markupAmount,
      totalCost: originalCost + markupAmount,
    };
  }, [assignedRooms, markupType, markupValue]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    setIsSubmitting(true);
    try {
      const newMarkup: MarkupSettings = {
        accommodationMarkupType: markupType,
        accommodationMarkupValue: markupValue,
      };

      await applyMarkup(newMarkup);
      onClose();
    } catch (error) {
      console.error("Failed to apply markup:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReset = () => {
    setMarkupType("percentage");
    setMarkupValue(0);
  };

  const handleRemoveMarkup = async () => {
    setIsSubmitting(true);
    try {
      await applyMarkup(null);
      onClose();
    } catch (error) {
      console.error("Failed to remove markup:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">
              💰 Globalny narzut/rabat
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              ✕
            </button>
          </div>

          {/* Formularz */}
          <form onSubmit={handleSubmit} className="p-6">
            <div className="space-y-4">
              {/* Hidden radio buttons - global markup can only be percentage type */}
              <input type="hidden" name="markupType" value="percentage" />

              {/* Wartość narzutu */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Wartość {markupType === "percentage" ? "(%)" : "(zł)"}
                </label>
                <div className="relative">
                  <input
                    type="number"
                    min={markupType === "percentage" ? "-100" : undefined}
                    max={markupType === "percentage" ? "1000" : undefined}
                    step={markupType === "percentage" ? "0.1" : "0.01"}
                    value={markupValue}
                    onChange={(e) =>
                      setMarkupValue(parseFloat(e.target.value) || 0)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 pr-8"
                    placeholder={markupType === "percentage" ? "10" : "100"}
                  />
                  <span className="absolute right-3 top-2 text-sm text-gray-500">
                    {markupType === "percentage" ? "%" : "zł"}
                  </span>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  {markupType === "percentage"
                    ? "Wartości ujemne = rabat. Np. -10% to rabat 10%"
                    : "Wartości ujemne = rabat. Np. -100 to rabat 100 zł"}
                </p>
              </div>

              {/* Podgląd kosztów */}
              {assignedRooms.length > 0 && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-gray-700 mb-3">
                    📊 Podgląd kosztów
                  </h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span>Koszt bazowy:</span>
                      <span>{costPreview.originalCost.toFixed(2)} zł</span>
                    </div>
                    <div className="flex justify-between">
                      <span>
                        {markupValue >= 0 ? "Narzut" : "Rabat"}(
                        {markupType === "percentage"
                          ? `${Math.abs(markupValue)}%`
                          : `${Math.abs(markupValue)} zł`}
                        ):
                      </span>
                      <span
                        className={
                          markupValue >= 0 ? "text-red-600" : "text-green-600"
                        }
                      >
                        {markupValue >= 0 ? "+" : "-"}
                        {Math.abs(costPreview.markupAmount).toFixed(2)} zł
                      </span>
                    </div>
                    <div className="flex justify-between font-medium border-t border-gray-200 pt-2">
                      <span>Koszt końcowy:</span>
                      <span>{costPreview.totalCost.toFixed(2)} zł</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Informacja o braku pokoi */}
              {assignedRooms.length === 0 && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-center">
                  <div className="text-yellow-700">
                    <div className="text-lg mb-1">🏨</div>
                    <div className="text-sm">
                      Brak przypisanych pokoi do obliczenia podglądu
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Przyciski */}
            <div className="flex items-center justify-between pt-6 mt-6 border-t border-gray-200">
              <div className="flex items-center space-x-2">
                <button
                  type="button"
                  onClick={handleReset}
                  className="px-3 py-2 text-sm text-gray-600 hover:text-gray-800 transition-colors"
                >
                  🔄 Resetuj
                </button>
                {markup && (
                  <button
                    type="button"
                    onClick={handleRemoveMarkup}
                    disabled={isSubmitting}
                    className="px-3 py-2 text-sm text-red-600 hover:text-red-800 transition-colors disabled:opacity-50"
                  >
                    🗑️ Usuń narzut
                  </button>
                )}
              </div>

              <div className="flex items-center space-x-3">
                <button
                  type="button"
                  onClick={onClose}
                  disabled={isSubmitting}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors disabled:opacity-50"
                >
                  Anuluj
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors disabled:opacity-50"
                >
                  {isSubmitting ? "Zapisuję..." : "Zastosuj"}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default GlobalMarkupModal;
